///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import {
    EngineAntennaDTO,
    EngineProductDTO,
    ExtendedProductdata,
    GtthKeyValueDTO,
    ProductMhTypeEnum,
    ProductRadioDTO,
    ProjectDTO
} from '../../../generated/shared-types.model';
import { ProjectService } from '../../../services/project/project.service';
import { ProductRadioService } from '../../../services/product/product-radio.service';
import { _cloneDeep, _isEqual } from '../../../shared/utils';
import { GtthLogicalTopology } from '../../../project-view-panel/logical-topology/entities/logical-topology';
import { forkJoin, Observable } from 'rxjs';
import { LogicalConfigModel } from '../../../services/logical-topology/logical-config.model';
import { tap } from 'rxjs/operators';

@Injectable()
export class ProjectDialogProductsService {
    public logicalConfig: LogicalConfigModel;
    public project: ProjectDTO;
    public engineRadioList: EngineProductDTO[];
    public mhProducts: ProductRadioDTO[];

    public availableBu: GtthKeyValueDTO[];
    public availableTu: GtthKeyValueDTO[];

    private initialProductsRadioConfig: EngineProductDTO[];

    constructor(
        private readonly projectService: ProjectService,
        private readonly productRadioService: ProductRadioService
    ) {}

    public loadRadioAndAntennaAndMhProducts(project: ProjectDTO, logicalConfig: LogicalConfigModel): Observable<any> {
        this.logicalConfig = logicalConfig;
        this.project = project;

        const linkProducts$ = this.projectService
            .getLinksProducts(this.project.id, this.project.customerId)
            .pipe(tap((products) => this.loadRadioConfigs(products)));

        const defaultProducts$ = this.projectService.getDefaultProducts(this.project.id, this.project.customerId).pipe(
            tap((res: ExtendedProductdata[][]) => {
                this.assignMissingProductData(res);
            })
        );

        const mhProducts$ = this.productRadioService.queryMh(this.project.id, this.project.customerId).pipe(
            tap((mhRadio: ProductRadioDTO[]) => {
                this.mhProducts = mhRadio;
                this.setDefaultMhProducts();
            })
        );

        return forkJoin([linkProducts$, defaultProducts$, mhProducts$]);
    }

    getChangedPdbList(): EngineProductDTO[] {
        const changedConfig = [];
        this.engineRadioList.sort((product1, product2) => product1.id - product2.id);
        this.initialProductsRadioConfig.sort((product1, product2) => product1.id - product2.id);
        for (let i = 0; i < this.engineRadioList.length; i++) {
            if (!_isEqual(this.engineRadioList[i], this.initialProductsRadioConfig[i])) {
                changedConfig.push(this.engineRadioList[i]);
            }
        }
        return changedConfig;
    }

    getAvailableAnennas(): EngineAntennaDTO[][] {
        const availableAntennas = [];

        this.engineRadioList.forEach((radio: EngineProductDTO) => {
            availableAntennas[radio.id] ??= [];
            availableAntennas[radio.id] = radio.pdbConfig.antennas;
            availableAntennas[radio.id].sort((antenna1, antenna2) => antenna1.id - antenna2.id);
        });

        return availableAntennas;
    }

    public getEngineProduct(usedProduct: number): EngineProductDTO {
        return this.engineRadioList.find((device) => device.id === usedProduct);
    }

    private loadRadioConfigs(res: EngineProductDTO[]): void {
        this.engineRadioList = res;
        this.engineRadioList.forEach((product) => {
            if (product.pdbConfig.channelConfigs && product.pdbConfig.channelConfigs.length > 1) {
                product.pdbConfig.channelConfigs.sort((a, b) => a.bandwidth - b.bandwidth);
            }
            product.pdbConfig.antennas.sort((antenna1, antenna2) => antenna1.id - antenna2.id);
        });
        this.initialProductsRadioConfig = _cloneDeep(this.engineRadioList);
    }

    private assignMissingProductData(defaultProducts: ExtendedProductdata[][]) {
        if (!this.logicalConfig.crs.properties.selectedProductsPerLayer) {
            this.logicalConfig.crs.properties.selectedProductsPerLayer = defaultProducts;
        } else {
            for (let i = 0; i < defaultProducts.length; i++) {
                if (!this.logicalConfig.crs.properties.selectedProductsPerLayer[i] && defaultProducts[i]) {
                    this.logicalConfig.crs.properties.selectedProductsPerLayer[i] = defaultProducts[i];
                }
            }
        }
    }

    private setDefaultMhProducts() {
        if (!this.mhProducts) {
            return;
        }

        this.availableBu = [];
        this.availableTu = [];

        this.mhProducts.forEach((mhRadio: ProductRadioDTO) => {
            if (mhRadio.mhType === ProductMhTypeEnum.BU) {
                this.availableBu.push({ key: mhRadio.id, value: mhRadio.name } as GtthKeyValueDTO);
            } else {
                this.availableTu.push({ key: mhRadio.id, value: mhRadio.name } as GtthKeyValueDTO);
            }
        });

        if (
            this.availableBu.length > 0 &&
            (!this.logicalConfig.crs.properties?.defaultBuProductPerLayer?.[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX] ||
                !this.availableBu.some(
                    (value) =>
                        value.key ===
                        this.logicalConfig.crs.properties.defaultBuProductPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX]
                ))
        ) {
            this.logicalConfig.crs.properties.defaultBuProductPerLayer = [this.availableBu[0].key];
        }

        if (
            this.availableTu.length > 0 &&
            (!this.logicalConfig.crs.properties?.defaultTuProductPerLayer?.[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX] ||
                !this.availableTu.some(
                    (value) =>
                        value.key ===
                        this.logicalConfig.crs.properties.defaultTuProductPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX]
                ))
        ) {
            this.logicalConfig.crs.properties.defaultTuProductPerLayer = [this.availableTu[0].key];
        }
    }
}
